import { useFormik } from 'formik';
import React, { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { Button } from '@/components/Elements';
import { MobileToken, WRONG_TOKEN, WRONG_TOKEN_VALIDATION } from '@/features/mobileToken';
import { BaseFormEntity, UserData } from '@/types';
import { checkPhone, renderField } from '@/utils/forms';
import { isAdminUser, isNewFreeUser } from '@/utils/helpers';

export type FormTokenCheckProps = {
  className?: string;
  initialValues: iFormTokenCheckFields | undefined;
  onSubmit: (values: iFormTokenCheckFields) => void;
  onRetryDelivery: () => void;
  mobileToken: MobileToken;
  userData: UserData;
};

export type iFormTokenCheckFields = {
  groupId: string;
  phone: string;
  token?: string;
} & BaseFormEntity;

const initialValuesDefault: iFormTokenCheckFields = {
  groupId: '',
  phone: '',
  token: '',
  formName: 'TokenCheck',
};

export const FormTokenCheck = ({
  className,
  initialValues,
  onSubmit,
  // onRetryDelivery,
  mobileToken,
  userData,
}: FormTokenCheckProps) => {
  const { t } = useTranslation();

  initialValuesDefault.groupId = userData.groupId;
  initialValuesDefault.phone = mobileToken.phone;

  const defaultValidationSchema = {
    phone: Yup.string()
      .min(3, `${t('Forms.minErrorChars')} 3 ${t('Forms.chars')}`)
      .test('test-phone', t('Forms.invalidFormat'), checkPhone)
      .required(t('Forms.required')),
    groupId: Yup.string().required(t('Forms.required')),
    token: Yup.string().required(t('Forms.required')),
  };

  const onDefaultRetryDelivery = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    // onRetryDelivery && onRetryDelivery();
    console.info('onDefaultRetryDelivery...');
  };

  const MainForm = () => {
    const onFormSubmit = (values: iFormTokenCheckFields) => {
      onSubmit && onSubmit(values);
    };

    const formikData = {
      initialValues: { ...initialValuesDefault, ...initialValues },
      validationSchema: Yup.object(defaultValidationSchema),
      onSubmit: onFormSubmit,
    };
    const formik: any = useFormik(formikData);

    return (
      <form onSubmit={formik.handleSubmit} className={className}>
        <h3 className="tmd__payments__form--h3">{t('Forms.TokenCheck.title')}</h3>
        <span className="text-lg italic mb-5">{t('Forms.TokenCheck.warningCase')}</span>
        <div className="mb-3 flex flex-wrap md:flex-nowrap justify-between gap-3">
          {renderField(formik, 'token', {
            customClasses: 'w-full md:w-1/2',
            required: true,
            autoFocus: true,
          })}
        </div>
        {renderField(formik, 'groupId', { type: 'hidden' })}
        {renderField(formik, 'phone', { type: 'hidden' })}
        <div className="mt-10 mb-3 flex justify-center lg:justify-between flex-wrap items-center">
          <div className="flex items-center mb-10 lg:mb-0 flex-wrap justify-center lg:justify-start w-full lg:w-auto">
            <Button
              variant="primary"
              type="submit"
              size="free"
              className="py-5 lg:py-3 w-full lg:w-[140px]"
            >
              {t('Forms.send')}
            </Button>
          </div>
          {canRetry(mobileToken, userData) && getRetry()}
        </div>
      </form>
    );
  };

  const getRetry = () => {
    // return (
    //   <a
    //     href="#"
    //     className="text-md-links no-underline hover:underline hover:text-md-links hidden"
    //     title={t('Forms.TokenCheck.retry')}
    //     onClick={onDefaultRetryDelivery}
    //   >
    //     {t('Forms.TokenCheck.retry')}
    //   </a>
    // );
    return (
      <a
        href="#"
        className="text-md-links no-underline hover:underline hover:text-md-links hidden"
        title={t('Forms.TokenCheck.retry')}
        onClick={onDefaultRetryDelivery}
      >
        {t('Forms.TokenCheck.retry')}
      </a>
    );
  };

  const isUsed = (mobileToken: MobileToken, userData: UserData): boolean => {
    if (isAdminUser(userData)) {
      return true;
    }
    if (mobileToken.usedAt) {
      return true;
    }

    return false;
  };

  const isBlocked = (mobileToken: MobileToken, userData: UserData): boolean => {
    if (isAdminUser(userData)) {
      return true;
    }
    if (
      typeof mobileToken.deliveries === 'undefined' ||
      parseInt(mobileToken.deliveries, 10) > 3 ||
      parseInt(mobileToken.deliveries, 10) === 0
    ) {
      return true;
    }

    if (typeof mobileToken.tries === 'undefined' || parseInt(mobileToken.tries, 10) >= 5) {
      return true;
    }

    return false;
  };

  const isBlockedByTime = (mobileToken: MobileToken, userData: UserData): boolean => {
    if (isBlocked(mobileToken, userData)) {
      return true;
    }

    if (
      typeof mobileToken.validAt === 'undefined' ||
      new Date(mobileToken.validAt).getTime() < new Date().getTime()
    ) {
      return true;
    }

    return false;
  };

  const canRetry = (mobileToken: MobileToken, userData: UserData): boolean => {
    if (
      isNewFreeUser(userData) &&
      (typeof mobileToken.deliveries === 'undefined' || parseInt(mobileToken.deliveries, 10) < 3)
    ) {
      return true;
    }

    return false;
  };

  const getError = (mobileToken: MobileToken): number => {
    if (typeof mobileToken.hasError !== 'undefined') {
      return mobileToken.hasError;
    }

    return 0;
  };

  const used: boolean = isUsed(mobileToken, userData);
  const blocked: boolean = isBlocked(mobileToken, userData);
  const blockedByTime: boolean = isBlockedByTime(mobileToken, userData);
  const errorToken: number = getError(mobileToken);

  return (
    <div className="w-full m-auto overflow px-4 py-0 lg:py-6">
      {used && <h3 className="md__payments__form--h3">{t('Forms.tokenChecked')}</h3>}
      {errorToken == WRONG_TOKEN && (
        <h3 className="md__payments__form--h3">
          {t('Forms.cantSendSmsError')} {canRetry(mobileToken, userData) && getRetry()}
        </h3>
      )}

      {errorToken == WRONG_TOKEN_VALIDATION && (
        <h3 className="md__payments__form--h3">{t('Forms.wrongToken')}</h3>
      )}

      {blocked && !used && !errorToken && (
        <h3 className="md__payments__form--h3">
          {t('Forms.blockBillingDetails')} {canRetry(mobileToken, userData) && getRetry()}
        </h3>
      )}
      {!used && !blocked && blockedByTime && (
        <h3 className="md__payments__form--h3">
          {t('Forms.TokenCheck.notValidTokenTime')} {canRetry(mobileToken, userData) && getRetry()}
        </h3>
      )}
      {!used && !blocked && !blockedByTime && <MainForm />}
    </div>
  );
};
